import type {
  Exam,
  MockScore,
  NoteInbox,
  Instant,
  Milestone,
  MogakSession,
  PhotoAsset,
  Plan,
  PlanItem,
  StudyLog,
  StudyStoreMap,
  StudyStoreName,
  Subject,
  UUID,
  WrongNote,
} from '@hoedokloop/domain';
import type {
  CapabilityResult,
  RepositoryListQuery,
  RepositoryMigration,
  RepositoryWrite,
  StudyRepository,
  TrashPurgeResult,
} from '@hoedokloop/platform-ports';
import Dexie, { type Table } from 'dexie';

import { HoedokLoopDatabase } from './database';

class RepositoryContractError extends Error {}

const tombstoneStores = new Set<StudyStoreName>([
  'exams',
  'subjects',
  'studyLogs',
  'wrongNotes',
  'photoAssets',
  'mockScores',
  'noteInbox',
]);

function failure<Value>(error: unknown, now: () => Instant): CapabilityResult<Value> {
  const name = error instanceof Error ? error.name : '';
  if (name === 'QuotaExceededError') {
    return { ok: false, reason: 'temporarily_unavailable', retryable: true, observedAt: now() };
  }
  return { ok: false, reason: 'failed', retryable: false, observedAt: now() };
}

type PersistedPhotoAsset = Omit<PhotoAsset, 'original' | 'thumbnail'> & {
  original: Uint8Array;
  thumbnail: Uint8Array;
};

function photoBlob(value: unknown, mimeType: string): Blob {
  if (value instanceof Blob) return value;
  if (value instanceof Uint8Array) return new Blob([value as BlobPart], { type: mimeType });
  if (value instanceof ArrayBuffer) return new Blob([value], { type: mimeType });
  throw new RepositoryContractError('Persisted photo bytes are invalid');
}

/** Decodes the binary representation used inside IndexedDB into the domain contract. */
export function materializeStudyStoreValue<Store extends StudyStoreName>(
  store: Store,
  value: StudyStoreMap[Store],
): StudyStoreMap[Store] {
  if (store !== 'photoAssets') return value;
  const photo = value as PhotoAsset;
  return {
    ...photo,
    original: photoBlob(photo.original, photo.mimeType),
    thumbnail: photoBlob(photo.thumbnail, photo.mimeType),
  } as StudyStoreMap[Store];
}

/**
 * WebKit can reject Blob/File structured-clones in IndexedDB. Store bytes at
 * rest and restore Blobs only at the repository boundary so every consumer
 * keeps the same PhotoAsset contract.
 */
export async function persistStudyStoreValue<Store extends StudyStoreName>(
  store: Store,
  value: StudyStoreMap[Store],
): Promise<StudyStoreMap[Store]> {
  if (store !== 'photoAssets') return value;
  const photo = value as PhotoAsset;
  const persisted: PersistedPhotoAsset = {
    ...photo,
    original: new Uint8Array(await photo.original.arrayBuffer()),
    thumbnail: new Uint8Array(await photo.thumbnail.arrayBuffer()),
  };
  return persisted as unknown as StudyStoreMap[Store];
}

export class IndexedDbStudyRepository implements StudyRepository {
  readonly database: HoedokLoopDatabase;
  readonly now: () => Instant;

  constructor(options: { database?: HoedokLoopDatabase; name?: string; now: () => Instant }) {
    this.database = options.database ?? new HoedokLoopDatabase(options.name);
    this.now = options.now;
  }

  private success<Value>(value: Value): CapabilityResult<Value> {
    return { ok: true, value, observedAt: this.now() };
  }

  private tables(): Table[] {
    return [
      this.database.exams,
      this.database.subjects,
      this.database.studyLogs,
      this.database.wrongNotes,
      this.database.photoAssets,
      this.database.plans,
      this.database.planItems,
      this.database.milestones,
      this.database.mogakSessions,
      this.database.mockScores,
      this.database.noteInbox,
    ];
  }

  private table<Store extends StudyStoreName>(store: Store): Table<StudyStoreMap[Store], UUID> {
    return this.database.table(store) as Table<StudyStoreMap[Store], UUID>;
  }

  async open(): Promise<CapabilityResult<{ schemaVersion: number }>> {
    try {
      await this.database.open();
      return this.success({ schemaVersion: this.database.verno });
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async close(): Promise<CapabilityResult<void>> {
    this.database.close();
    return this.success(undefined);
  }

  async get<Store extends StudyStoreName>(
    store: Store,
    id: UUID,
  ): Promise<CapabilityResult<StudyStoreMap[Store] | undefined>> {
    try {
      const value = await this.table(store).get(id);
      return this.success(
        value === undefined ? undefined : materializeStudyStoreValue(store, value),
      );
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async list<Store extends StudyStoreName>(
    store: Store,
    query: RepositoryListQuery = {},
  ): Promise<CapabilityResult<StudyStoreMap[Store][]>> {
    try {
      let values = await this.table(store).toArray();
      values = values.filter((value) => {
        const candidate = value as StudyStoreMap[StudyStoreName] & {
          examId?: UUID;
          subjectId?: UUID;
          updatedAt?: Instant;
          deletedAt?: Instant;
        };
        if (!query.includeDeleted && candidate.deletedAt !== undefined) return false;
        if (query.examId !== undefined && candidate.examId !== query.examId) return false;
        if (query.subjectId !== undefined && candidate.subjectId !== query.subjectId) return false;
        if (query.changedAfter !== undefined && candidate.updatedAt !== undefined) {
          if (candidate.updatedAt <= query.changedAfter) return false;
        }
        return true;
      });
      if (query.limit !== undefined) values = values.slice(0, query.limit);
      return this.success(values.map((value) => materializeStudyStoreValue(store, value)));
    } catch (error) {
      return failure(error, this.now);
    }
  }

  private async requireExam(id: UUID): Promise<Exam> {
    const exam = await this.database.exams.get(id);
    if (!exam) throw new RepositoryContractError('Referenced exam does not exist');
    return exam;
  }

  private async requireSubject(id: UUID, examId: UUID): Promise<Subject> {
    const subject = await this.database.subjects.get(id);
    if (!subject || subject.examId !== examId) {
      throw new RepositoryContractError('Referenced subject does not belong to exam');
    }
    return subject;
  }

  private async validate(
    store: StudyStoreName,
    raw: StudyStoreMap[StudyStoreName],
    pendingPhotoIds: ReadonlySet<UUID> = new Set(),
  ): Promise<void> {
    if (store === 'mockScores') {
      const score = raw as MockScore;
      await this.requireExam(score.examId);
      for (const item of score.scores) {
        const subject = await this.database.subjects.get(item.subjectId);
        if (!subject || subject.examId !== score.examId)
          throw new RepositoryContractError('Score subject mismatch');
      }
      if (
        !score.title.trim() ||
        !score.scores.length ||
        score.scores.some(
          (item) => !Number.isFinite(item.score) || item.score < 0 || item.score > 100,
        )
      )
        throw new RepositoryContractError('Invalid score');
      return;
    }
    if (store === 'noteInbox') {
      const item = raw as NoteInbox;
      if (
        !pendingPhotoIds.has(item.photoAssetId) &&
        !(await this.database.photoAssets.get(item.photoAssetId))
      )
        throw new RepositoryContractError('Missing inbox photo');
      return;
    }
    if (store === 'exams') {
      const exam = raw as Exam;
      if (!exam.title.trim() || !exam.timezone) throw new RepositoryContractError('Invalid exam');
      return;
    }
    if (store === 'subjects') {
      const subject = raw as Subject;
      await this.requireExam(subject.examId);
      if (
        !subject.name.trim() ||
        (subject.totalUnits !== null &&
          (!Number.isInteger(subject.totalUnits) ||
            subject.totalUnits < 1 ||
            subject.totalUnits > 1_000_000))
      ) {
        throw new RepositoryContractError('Invalid subject');
      }
      const previous = await this.database.subjects.get(subject.id);
      if (previous && previous.totalUnits !== null && subject.totalUnits === null) {
        throw new RepositoryContractError('Known subject total cannot be removed');
      }
      if (
        previous &&
        subject.totalUnits !== null &&
        (previous.totalUnits === null || subject.totalUnits < previous.totalUnits)
      ) {
        const newTotalUnits = subject.totalUnits;
        const conflicting = await this.database.studyLogs
          .where('subjectId')
          .equals(subject.id)
          .and((log) => log.deletedAt === undefined && log.unitEnd > newTotalUnits)
          .count();
        if (conflicting > 0) {
          throw new RepositoryContractError('Subject shrink requires migration review');
        }
      }
      return;
    }
    if (store === 'studyLogs') {
      const log = raw as StudyLog;
      const subject = await this.requireSubject(log.subjectId, log.examId);
      if (
        log.round < 1 ||
        !Number.isInteger(log.round) ||
        log.unitStart < 1 ||
        log.unitEnd < log.unitStart ||
        log.unitEnd > (subject.totalUnits ?? 1_000_000) ||
        (log.minutes !== undefined &&
          (!Number.isInteger(log.minutes) || log.minutes < 1 || log.minutes > 1_440))
      ) {
        throw new RepositoryContractError('Invalid study log');
      }
      return;
    }
    if (store === 'wrongNotes') {
      const note = raw as WrongNote;
      await this.requireSubject(note.subjectId, note.examId);
      if (!note.text?.trim() && note.photoAssetId === undefined) {
        throw new RepositoryContractError('Wrong note requires text or photo');
      }
      if (
        note.photoAssetId !== undefined &&
        !pendingPhotoIds.has(note.photoAssetId) &&
        !(await this.database.photoAssets.get(note.photoAssetId))
      ) {
        throw new RepositoryContractError('Referenced photo does not exist');
      }
      return;
    }
    if (store === 'photoAssets') {
      const photo = raw as PhotoAsset;
      const supportedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
      if (
        photo.byteLength < 1 ||
        photo.byteLength !== photo.original.size ||
        photo.width < 1 ||
        photo.height < 1 ||
        !supportedTypes.has(photo.mimeType) ||
        photo.original.type !== photo.mimeType ||
        photo.thumbnail.type !== photo.mimeType ||
        photo.thumbnail.size < 1 ||
        !/^[a-f0-9]{64}$/u.test(photo.sha256)
      ) {
        throw new RepositoryContractError('Invalid photo asset');
      }
      return;
    }
    if (store === 'plans') {
      const plan = raw as Plan;
      await this.requireExam(plan.examId);
      if (plan.version < 1 || !Number.isInteger(plan.version)) {
        throw new RepositoryContractError('Invalid plan version');
      }
      return;
    }
    if (store === 'planItems') {
      const item = raw as PlanItem;
      const plan = await this.database.plans.get(item.planId);
      if (!plan) throw new RepositoryContractError('Referenced plan does not exist');
      const subject = await this.requireSubject(item.subjectId, plan.examId);
      if (
        item.unitStart < 1 ||
        item.unitEnd < item.unitStart ||
        subject.totalUnits === null ||
        item.unitEnd > subject.totalUnits
      ) {
        throw new RepositoryContractError('Invalid plan range');
      }
      return;
    }
    if (store === 'milestones') {
      const milestone = raw as Milestone;
      if (milestone.examId !== undefined) await this.requireExam(milestone.examId);
      const identity = [
        milestone.key,
        milestone.examId ?? '-',
        milestone.sourceEntityId ?? '-',
      ].join(':');
      const duplicate = (await this.database.milestones.toArray()).some(
        (existing) =>
          existing.id !== milestone.id &&
          [existing.key, existing.examId ?? '-', existing.sourceEntityId ?? '-'].join(':') ===
            identity,
      );
      if (duplicate) throw new RepositoryContractError('Duplicate milestone identity');
      return;
    }
    if (store === 'mogakSessions') {
      const session = raw as MogakSession;
      await this.requireExam(session.examId);
      if (session.activeMinutes < 0 || !Number.isInteger(session.activeMinutes)) {
        throw new RepositoryContractError('Invalid room duration');
      }
    }
  }

  private async putValidated<Store extends StudyStoreName>(
    store: Store,
    value: StudyStoreMap[Store],
    pendingPhotoIds?: ReadonlySet<UUID>,
  ): Promise<StudyStoreMap[Store]> {
    const existing = await this.table(store).get(value.id);
    if ('revision' in value) {
      const revision = value.revision;
      const existingRevision = existing && 'revision' in existing ? existing.revision : undefined;
      if (existingRevision === undefined ? revision !== 1 : revision !== existingRevision + 1) {
        throw new RepositoryContractError('Optimistic revision conflict');
      }
    }
    await Dexie.waitFor(this.validate(store, value, pendingPhotoIds));
    const persisted = await Dexie.waitFor(persistStudyStoreValue(store, value));
    await this.table(store).put(persisted);
    return value;
  }

  async put<Store extends StudyStoreName>(
    store: Store,
    value: StudyStoreMap[Store],
  ): Promise<CapabilityResult<StudyStoreMap[Store]>> {
    try {
      const saved = await this.database.transaction('rw', this.tables(), () =>
        this.putValidated(store, value),
      );
      return this.success(saved);
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async putMany<Store extends StudyStoreName>(
    store: Store,
    values: StudyStoreMap[Store][],
  ): Promise<CapabilityResult<StudyStoreMap[Store][]>> {
    try {
      const saved = await this.database.transaction('rw', this.tables(), async () => {
        const result: StudyStoreMap[Store][] = [];
        for (const value of values) result.push(await this.putValidated(store, value));
        return result;
      });
      return this.success(saved);
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async putBatch(writes: readonly RepositoryWrite[]): Promise<CapabilityResult<void>> {
    if (writes.length === 0)
      return failure(new RepositoryContractError('Writes required'), this.now);
    try {
      await this.database.transaction('rw', this.tables(), async () => {
        const pendingPhotoIds = new Set(
          writes.flatMap((write) => (write.store === 'photoAssets' ? [write.value.id] : [])),
        );
        for (const write of writes) {
          await this.putValidated(
            write.store,
            write.value as StudyStoreMap[typeof write.store],
            pendingPhotoIds,
          );
        }
      });
      return this.success(undefined);
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async remove(
    store: StudyStoreName,
    id: UUID,
    deletedAt: Instant,
  ): Promise<CapabilityResult<void>> {
    if (!tombstoneStores.has(store)) {
      return failure(new RepositoryContractError('Store does not support tombstones'), this.now);
    }
    try {
      await this.database.transaction('rw', this.tables(), async () => {
        const table = this.table(store);
        const existing = await table.get(id);
        if (!existing) throw new RepositoryContractError('Entity does not exist');
        const revision = 'revision' in existing ? { revision: existing.revision + 1 } : {};
        const updatedAt = 'updatedAt' in existing ? { updatedAt: deletedAt } : {};
        await table.put({
          ...existing,
          ...revision,
          ...updatedAt,
          deletedAt,
        } as StudyStoreMap[typeof store]);
      });
      return this.success(undefined);
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async deletePermanent(store: StudyStoreName, id: UUID): Promise<CapabilityResult<void>> {
    try {
      await this.table(store).delete(id);
      return this.success(undefined);
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async transaction<Value>(
    stores: StudyStoreName[],
    operation: () => Promise<Value>,
  ): Promise<CapabilityResult<Value>> {
    if (stores.length === 0)
      return failure(new RepositoryContractError('Stores required'), this.now);
    try {
      const value = await this.database.transaction('rw', this.tables(), operation);
      return this.success(value);
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async purgeTrash(before: Instant): Promise<CapabilityResult<TrashPurgeResult>> {
    try {
      const purged = await this.database.transaction('rw', this.tables(), async () => {
        const expiredExams = (await this.database.exams.toArray()).filter(
          (exam) =>
            exam.deletedAt !== undefined && exam.deletedAt <= before && !exam.retainedArchive,
        );
        const expiredExamIds = new Set(expiredExams.map((exam) => exam.id));
        const subjects = (await this.database.subjects.toArray()).filter((subject) =>
          expiredExamIds.has(subject.examId),
        );
        const studyLogs = (await this.database.studyLogs.toArray()).filter(
          (log) =>
            expiredExamIds.has(log.examId) ||
            (log.deletedAt !== undefined && log.deletedAt <= before),
        );
        const wrongNotes = (await this.database.wrongNotes.toArray()).filter(
          (note) =>
            expiredExamIds.has(note.examId) ||
            (note.deletedAt !== undefined && note.deletedAt <= before),
        );
        const plans = (await this.database.plans.toArray()).filter((plan) =>
          expiredExamIds.has(plan.examId),
        );
        const planIds = new Set(plans.map((plan) => plan.id));
        const planItems = (await this.database.planItems.toArray()).filter((item) =>
          planIds.has(item.planId),
        );
        const milestones = (await this.database.milestones.toArray()).filter(
          (milestone) => milestone.examId !== undefined && expiredExamIds.has(milestone.examId),
        );
        const mogakSessions = (await this.database.mogakSessions.toArray()).filter((session) =>
          expiredExamIds.has(session.examId),
        );
        const scores = (await this.database.mockScores.toArray()).filter(
          (score) =>
            expiredExamIds.has(score.examId) ||
            (score.deletedAt !== undefined && score.deletedAt <= before),
        );
        await this.database.mockScores.bulkDelete(scores.map((score) => score.id));
        const inbox = (await this.database.noteInbox.toArray()).filter(
          (item) => item.deletedAt !== undefined && item.deletedAt <= before,
        );
        await this.database.noteInbox.bulkDelete(inbox.map((item) => item.id));

        await this.database.planItems.bulkDelete(planItems.map((item) => item.id));
        await this.database.plans.bulkDelete(plans.map((plan) => plan.id));
        await this.database.milestones.bulkDelete(milestones.map((milestone) => milestone.id));
        await this.database.mogakSessions.bulkDelete(mogakSessions.map((session) => session.id));
        await this.database.studyLogs.bulkDelete(studyLogs.map((log) => log.id));
        await this.database.wrongNotes.bulkDelete(wrongNotes.map((note) => note.id));
        await this.database.subjects.bulkDelete(subjects.map((subject) => subject.id));
        await this.database.exams.bulkDelete(expiredExams.map((exam) => exam.id));

        const candidatePhotoIds = new Set(
          wrongNotes.flatMap((note) =>
            note.photoAssetId === undefined ? [] : [note.photoAssetId],
          ),
        );
        for (const photo of await this.database.photoAssets.toArray()) {
          if (photo.deletedAt !== undefined && photo.deletedAt <= before) {
            candidatePhotoIds.add(photo.id);
          }
        }
        const orphanedPhotoIds: UUID[] = [];
        for (const photoId of candidatePhotoIds) {
          const references = await this.database.wrongNotes
            .where('photoAssetId')
            .equals(photoId)
            .count();
          if (
            references === 0 &&
            (await this.database.noteInbox.where('photoAssetId').equals(photoId).count()) === 0
          )
            orphanedPhotoIds.push(photoId);
        }
        await this.database.photoAssets.bulkDelete(orphanedPhotoIds);

        return {
          exams: expiredExams.length,
          subjects: subjects.length,
          studyLogs: studyLogs.length,
          wrongNotes: wrongNotes.length,
          photoAssets: orphanedPhotoIds.length,
          plans: plans.length,
          planItems: planItems.length,
          milestones: milestones.length,
          mogakSessions: mogakSessions.length,
        };
      });
      return this.success(purged);
    } catch (error) {
      return failure(error, this.now);
    }
  }

  async migrations(): Promise<CapabilityResult<RepositoryMigration[]>> {
    return this.success([
      { fromVersion: 0, toVersion: 1, appliedAt: this.now() },
      { fromVersion: 1, toVersion: 2, appliedAt: this.now() },
    ]);
  }

  async collectOrphanedPhotos(
    graceBefore: Instant,
  ): Promise<CapabilityResult<{ deleted: number }>> {
    try {
      const deleted = await this.database.transaction('rw', this.tables(), async () => {
        const candidates = await this.database.photoAssets
          .filter((photo) => photo.deletedAt !== undefined && photo.deletedAt <= graceBefore)
          .toArray();
        let count = 0;
        for (const photo of candidates) {
          const blockingReferences = await this.database.wrongNotes
            .where('photoAssetId')
            .equals(photo.id)
            .and((note) => note.deletedAt === undefined || note.deletedAt > graceBefore)
            .count();
          if (
            blockingReferences === 0 &&
            (await this.database.noteInbox.where('photoAssetId').equals(photo.id).count()) === 0
          ) {
            await this.database.photoAssets.delete(photo.id);
            count += 1;
          }
        }
        return count;
      });
      return this.success({ deleted });
    } catch (error) {
      return failure(error, this.now);
    }
  }
}
